import { ErrorHandler } from '@angular/core';

/**
 * Messages the browser emits for the benign "ResizeObserver couldn't deliver
 * all notifications within one frame" condition. ag-grid's layout passes
 * trigger it routinely; the browser recovers on the next frame and no state
 * is lost. Chromium and Firefox use the first wording, older WebKit/Chrome
 * builds the second.
 */
const RESIZE_OBSERVER_LOOP_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
];

/** True when the error (or its cause chain) is the benign ResizeObserver loop event. */
export function isResizeObserverLoopError(error: unknown, depth = 0): boolean {
  if (error == null || depth > 5) {
    return false;
  }
  const message =
    typeof error === 'string'
      ? error
      : typeof (error as { message?: unknown }).message === 'string'
        ? (error as { message: string }).message
        : '';
  if (RESIZE_OBSERVER_LOOP_MESSAGES.some((benign) => message.includes(benign))) {
    return true;
  }
  // Angular's global error listener wraps ErrorEvents without an error object
  // in a new Error whose `cause` carries the original message.
  return isResizeObserverLoopError((error as { cause?: unknown }).cause, depth + 1);
}

/**
 * Drop-in replacement for Angular's default ErrorHandler that swallows only
 * the benign ResizeObserver loop error (which the preview frame would
 * otherwise report as an app crash) and delegates everything else unchanged.
 */
export class ResizeObserverTolerantErrorHandler extends ErrorHandler {
  override handleError(error: unknown): void {
    if (isResizeObserverLoopError(error)) {
      return;
    }
    super.handleError(error);
  }
}
